import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import {
  ReportDetail,
  ReportingMenuResponse,
  ReportMenu,
} from '../../workspace-v2/analytics/reporting-v2/types/reporting.types';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class SelfTrainerV2Service {
  selectedMenuPath = new BehaviorSubject<ReportMenu[]>([]);
  private readonly menuState = new BehaviorSubject<ReportingMenuResponse>(null);
  private readonly isLoading = new BehaviorSubject<boolean>(false);
  private menuPathCache: Record<string, ReportMenu[]> = {};
  private reportPathCache: Record<string, ReportMenu[]> = {};

  constructor(private readonly apiService: ApiService) {}

  getMenuState(): Observable<ReportingMenuResponse> {
    return this.menuState.asObservable();
  }

  getLoadingState(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  findMenuPathById(menuId: string): ReportMenu[] {
    if (this.menuPathCache[menuId]) {
      return this.menuPathCache[menuId];
    }

    if (!this.menuState.value?.menus) return [];

    const findPath = (menus: ReportMenu[], targetId: string, currentPath: ReportMenu[] = []): ReportMenu[] | null => {
      for (const menu of menus) {
        const newPath = currentPath.concat(menu);

        if (menu.id === targetId) {
          return newPath;
        }

        if (menu.subMenus?.length > 0) {
          const foundPath = findPath(menu.subMenus, targetId, newPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };

    const path = findPath(this.menuState.value.menus, menuId) || [];
    this.menuPathCache[menuId] = path;
    return path;
  }

  findMenuPathByReportId(reportId: string): ReportMenu[] {
    if (this.reportPathCache[reportId]) {
      return this.reportPathCache[reportId];
    }

    if (!this.menuState.value?.menus) return [];

    const findPath = (menus: ReportMenu[], targetId: string, currentPath: ReportMenu[] = []): ReportMenu[] | null => {
      for (const menu of menus) {
        const newPath = currentPath.concat(menu);
        if (menu.items?.some((item) => item.id === targetId)) {
          return newPath;
        }

        if (menu.subMenus?.length > 0) {
          const foundPath = findPath(menu.subMenus, targetId, newPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };

    const path = findPath(this.menuState.value.menus, reportId) || [];
    this.reportPathCache[reportId] = path;
    return path;
  }

  getSelfTrainerList(refetch = false): Observable<ReportingMenuResponse> {
    if (this.menuState.value && !refetch) {
      return of(this.menuState.value);
    }

    this.isLoading.next(true);
    return this.apiService.httpGet('/api/selfTrainer', true).pipe(
      map((response: any) => response as ReportingMenuResponse),
      tap((response) => {
        this.menuState.next(response);
        this.isLoading.next(false);
      }),
      catchError((error) => {
        console.error('Error fetching report menus:', error);
        this.isLoading.next(false);
        throw error;
      })
    );
  }

  getSelfTrainerReport(reportId: string): Observable<ReportDetail> {
    return this.apiService
      .httpGet(`/api/selfTrainer/${reportId}`, true)
      .pipe(map((response: any) => response as ReportDetail));
  }
}
